import React, { FC, useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { z } from 'zod';
import { FeedbackPopup, FeedbackType } from 'src/components/feedback_popup';
import { Warning } from 'src/components/icons/warning';
import { RadioGroupField } from 'src/components/radio_group';
import { useWorkflow } from 'src/distribution_platforms/features/hooks/use_workflow';
import { LdtOrderDetailFieldsFragment, OrderStatus } from 'src/types';

const rejectReportSchema = z.object({
  reason: z.string().min(1, 'Rejection reason is required'),
  revertStatus: z
    .enum([
      OrderStatus.QualityControlPending,
      OrderStatus.LabProcessing,
    ] as const)
    .optional(),
});

export type RejectReportFormData = z.infer<typeof rejectReportSchema>;

interface RejectReportDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: RejectReportFormData) => void;
  order: LdtOrderDetailFieldsFragment;
}

const REVERT_STATUS_LABELS: Record<string, string> = {
  [OrderStatus.QualityControlPending]: 'Revert order to QC pending',
  [OrderStatus.LabProcessing]: 'Revert order to Lab Processing',
};

const REVERT_STATUS_OPTIONS = [
  {
    value: OrderStatus.QualityControlPending,
    name: REVERT_STATUS_LABELS[OrderStatus.QualityControlPending],
  },
  {
    value: OrderStatus.LabProcessing,
    name: REVERT_STATUS_LABELS[OrderStatus.LabProcessing],
  },
];

/**
 * Dialog for rejecting a draft report.
 * - Required rejection reason (text)
 * - Optional revert status (QC Pending / Lab Processing) when order does not have a revised report
 * - Confirm step before submit
 */
export const RejectReportDialog: FC<RejectReportDialogProps> = ({
  open,
  onClose,
  onSubmit,
  order,
}) => {
  const workflow = useWorkflow();
  const hasRevisedReport = order.hasRevisedReport ?? false;
  const showRevertStatus = !hasRevisedReport;

  const [confirmOpen, setConfirmOpen] = useState(false);

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
    reset,
    watch,
  } = useForm<RejectReportFormData>({
    resolver: zodResolver(rejectReportSchema),
    defaultValues: {
      reason: '',
      revertStatus: showRevertStatus
        ? OrderStatus.QualityControlPending
        : undefined,
    },
  });

  const revertStatus = watch('revertStatus');

  const handleClose = useCallback(() => {
    reset();
    setConfirmOpen(false);
    onClose();
  }, [onClose, reset]);

  const handleFormSubmit = useCallback((_data: RejectReportFormData) => {
    setConfirmOpen(true);
  }, []);

  const handleConfirmSubmit = useCallback(() => {
    const data = getValues();
    onSubmit(data);
    handleClose();
    setConfirmOpen(false);
  }, [getValues, onSubmit, handleClose]);

  const handleConfirmClose = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  const getConfirmTitle = useCallback(() => {
    if (hasRevisedReport) {
      return 'Confirm to reject the report. The order will be reverted to its previous status (e.g., Report Ready, Report Faxed, or Report Fax Failed).';
    }

    return `Confirm to update the order status to "${workflow.getStatusDisplayName(revertStatus!)}"`;
  }, [hasRevisedReport, revertStatus, workflow]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='sm'
        fullWidth
        data-testid='reject-report-dialog'
      >
        <DialogTitle sx={{ textAlign: 'left' }}>Report Rejection</DialogTitle>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogContent sx={{ p: 0 }}>
            <Stack sx={{ mt: 1 }}>
              <Controller
                name='reason'
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label='Report Rejection Reason'
                    required
                    fullWidth
                    multiline
                    rows={4}
                    error={!!errors.reason}
                    helperText={errors.reason?.message ?? ' '}
                    slotProps={{
                      inputLabel: { shrink: true },
                    }}
                  />
                )}
              />
              {showRevertStatus && (
                <Controller
                  name='revertStatus'
                  control={control}
                  render={({ field }) => (
                    <RadioGroupField
                      name={field.name}
                      value={field.value ?? OrderStatus.QualityControlPending}
                      options={REVERT_STATUS_OPTIONS}
                      required
                      row={false}
                      onChange={(e) =>
                        field.onChange(e.target.value as OrderStatus)
                      }
                      onBlur={field.onBlur}
                      validationText={errors.revertStatus?.message ?? ' '}
                    />
                  )}
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'flex-end', gap: 2 }}>
            <Button
              onClick={handleClose}
              variant='text'
              size='large'
              data-testid='reject-report-cancel'
            >
              CANCEL
            </Button>
            <Button
              type='submit'
              variant='contained'
              size='large'
              data-testid='reject-report-submit'
            >
              SUBMIT
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <FeedbackPopup
        data={{
          primaryButtonText: 'YES, I CONFIRM',
          secondaryButtonText: 'No',
          title: getConfirmTitle(),
          subtitle: '',
          icon: Warning,
          type: FeedbackType.Primary,
        }}
        isDialogOpen={confirmOpen}
        onPrimaryButtonClick={handleConfirmSubmit}
        onSecondaryButtonClick={handleConfirmClose}
        handleDialogClose={handleConfirmClose}
        primaryButtonTestId='reject-report-confirm'
        secondaryButtonTestId='reject-report-no'
      />
    </>
  );
};
